// flowGeometry — the pure geometry behind the sankey flow overlays. Given
// measured column/row positions and a flat list of flows, produce the SVG path
// d-strings and node rects. Kept free of React/DOM so the non-trivial bits
// (proportional node stacking, stroke normalization, on-screen filtering) are
// unit-testable in isolation; the overlay components only apply hover alpha and
// render.
//
// Two orientations share the shape { paths, nodes }:
//   • composition — horizontal ribbons across column gutters (nodes are vertical
//     bars at column edges, flows stack along Y).
//   • position — vertical ribbons down the stacked levels (nodes are horizontal
//     bars at row edges, flows stack along X).

const COMPOSITION_NODE_W = 4;
const COMPOSITION_NODE_MAX_H = 20;
const POSITION_NODE_H = 4;
const POSITION_NODE_MAX_W = 46;

// Ribbon thickness from a flow's share of the biggest flow. The two overlays
// were tuned independently — keep their exact curves.
const strokeComposition = (norm) => Math.max(0.75, Math.min(2.5, 0.75 + Math.pow(norm, 0.66) * 1.5));
const strokePosition = (norm) => Math.max(0.75, Math.min(2.6, 0.75 + Math.pow(norm, 0.6) * 1.7));

/**
 * Horizontal flows between composition columns.
 * @param {number} width
 * @param {number} height
 * @param {Array<{leftX:number, rightX:number, rows:Array<{y:number, value:number}>}>} columnGeom
 * @param {Array<{pair:number, aRow:number, bRow:number, count:number}>} flows
 * @returns {{paths:Array, nodes:Array}}
 */
export function buildCompositionFlowGeometry(width, height, columnGeom, flows) {
  const paths = [];
  const nodes = [];
  if (!(width > 0) || !(height > 0) || !columnGeom?.length || !flows?.length) return { paths, nodes };

  const globalMax = flows.reduce((m, f) => Math.max(m, f.count), 0) || 1;
  const colMax = columnGeom.map((c) => c.rows.reduce((m, r) => Math.max(m, r.value), 0) || 1);
  const nodeH = (ci, ri) => Math.max(6, ((columnGeom[ci]?.rows[ri]?.value || 0) / colMax[ci]) * COMPOSITION_NODE_MAX_H);

  const byPair = new Map();
  for (const f of flows) { if (!byPair.has(f.pair)) byPair.set(f.pair, []); byPair.get(f.pair).push(f); }

  for (const [pair, pf] of byPair) {
    const A = columnGeom[pair];
    const B = columnGeom[pair + 1];
    if (!A || !B) continue;
    const sx = A.rightX;
    const tx = B.leftX - COMPOSITION_NODE_W;
    const srcTot = {};
    const tgtTot = {};
    for (const f of pf) { srcTot[f.aRow] = (srcTot[f.aRow] || 0) + f.count; tgtTot[f.bRow] = (tgtTot[f.bRow] || 0) + f.count; }
    const srcTop = {};
    const tgtTop = {};
    const srcUsed = {};
    const tgtUsed = {};
    for (const r in srcTot) { const y = A.rows[r]?.y; if (y != null) { srcTop[r] = y - nodeH(pair, +r) / 2; srcUsed[r] = 0; } }
    for (const r in tgtTot) { const y = B.rows[r]?.y; if (y != null) { tgtTop[r] = y - nodeH(pair + 1, +r) / 2; tgtUsed[r] = 0; } }

    // Largest flows first so a row's node stacks big→small (stable read).
    for (const f of [...pf].sort((a, b) => b.count - a.count)) {
      if (srcTop[f.aRow] == null || tgtTop[f.bRow] == null || !(f.count > 0)) continue;
      const sSlice = (f.count / srcTot[f.aRow]) * nodeH(pair, f.aRow);
      const tSlice = (f.count / tgtTot[f.bRow]) * nodeH(pair + 1, f.bRow);
      const y0 = srcTop[f.aRow] + srcUsed[f.aRow] + sSlice / 2; srcUsed[f.aRow] += sSlice;
      const y1 = tgtTop[f.bRow] + tgtUsed[f.bRow] + tSlice / 2; tgtUsed[f.bRow] += tSlice;
      const x0 = sx + COMPOSITION_NODE_W;
      const x1 = tx;
      const cpx = (x0 + x1) / 2;
      paths.push({ d: `M ${x0} ${y0} C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`, sw: strokeComposition(f.count / globalMax), pair, aRow: f.aRow, bRow: f.bRow });
    }
    for (const r in srcTot) { const h = nodeH(pair, +r); const y = A.rows[r]?.y; if (y != null) nodes.push({ x: sx, y: y - h / 2, w: COMPOSITION_NODE_W, h, col: pair, row: +r }); }
    for (const r in tgtTot) { const h = nodeH(pair + 1, +r); const y = B.rows[r]?.y; if (y != null) nodes.push({ x: tx, y: y - h / 2, w: COMPOSITION_NODE_W, h, col: pair + 1, row: +r }); }
  }
  return { paths, nodes };
}

/**
 * Vertical flows down the position stack.
 * @param {number} width
 * @param {number} height
 * @param {Array<{top:number, bottom:number, cells:Array<{id:string, cx:number, w:number, value:number}>}>} rows  per level, indexed by level
 * @param {Array<{pair:number, aId:string, bId:string, count:number, spine:boolean}>} flows
 * @returns {{paths:Array, nodes:Array}}
 */
export function buildPositionFlowGeometry(width, height, rows, flows) {
  const paths = [];
  const nodes = [];
  if (!(width > 0) || !(height > 0) || !rows?.length || !flows?.length) return { paths, nodes };

  const globalMax = flows.reduce((m, f) => Math.max(m, f.count), 0) || 1;
  const cellById = rows.map((r) => {
    const m = new Map();
    for (const c of r?.cells || []) m.set(String(c.id), c);
    return m;
  });
  const rowMax = rows.map((r) => (r?.cells || []).reduce((m, c) => Math.max(m, c.value || 0), 0) || 1);
  const nodeW = (ri, cell) => Math.max(6, ((cell.value || 0) / rowMax[ri]) * POSITION_NODE_MAX_W);

  // Keep only flows whose endpoints are both currently on-screen.
  const byPair = new Map();
  for (const f of flows) {
    const src = cellById[f.pair]?.get(String(f.aId));
    const tgt = cellById[f.pair + 1]?.get(String(f.bId));
    if (!src || !tgt || !(f.count > 0)) continue;
    if (!byPair.has(f.pair)) byPair.set(f.pair, []);
    byPair.get(f.pair).push({ ...f, src, tgt });
  }

  for (const [pair, pf] of byPair) {
    const A = rows[pair];
    const B = rows[pair + 1];
    if (!A || !B) continue;
    const sBot = A.bottom;
    const tTop = B.top;
    const srcTot = {};
    const tgtTot = {};
    for (const f of pf) { srcTot[f.aId] = (srcTot[f.aId] || 0) + f.count; tgtTot[f.bId] = (tgtTot[f.bId] || 0) + f.count; }
    const srcLeft = {};
    const tgtLeft = {};
    const srcUsed = {};
    const tgtUsed = {};
    for (const f of pf) {
      if (srcLeft[f.aId] == null) { srcLeft[f.aId] = f.src.cx - nodeW(pair, f.src) / 2; srcUsed[f.aId] = 0; }
      if (tgtLeft[f.bId] == null) { tgtLeft[f.bId] = f.tgt.cx - nodeW(pair + 1, f.tgt) / 2; tgtUsed[f.bId] = 0; }
    }
    // Largest flows first so a cell's node stacks big→small (stable read).
    for (const f of [...pf].sort((a, b) => b.count - a.count)) {
      const sW = nodeW(pair, f.src);
      const tW = nodeW(pair + 1, f.tgt);
      const sS = (f.count / srcTot[f.aId]) * sW;
      const tS = (f.count / tgtTot[f.bId]) * tW;
      const x0 = srcLeft[f.aId] + srcUsed[f.aId] + sS / 2; srcUsed[f.aId] += sS;
      const x1 = tgtLeft[f.bId] + tgtUsed[f.bId] + tS / 2; tgtUsed[f.bId] += tS;
      const y0 = sBot + POSITION_NODE_H;
      const y1 = tTop - POSITION_NODE_H;
      const cpy = (y0 + y1) / 2;
      paths.push({ d: `M ${x0} ${y0} C ${x0} ${cpy}, ${x1} ${cpy}, ${x1} ${y1}`, sw: strokePosition(f.count / globalMax), pair, aId: f.aId, bId: f.bId, spine: f.spine });
    }
    for (const id in srcTot) { const cell = cellById[pair].get(id); const w = nodeW(pair, cell); nodes.push({ x: cell.cx - w / 2, y: sBot, w, h: POSITION_NODE_H, row: pair, id }); }
    for (const id in tgtTot) { const cell = cellById[pair + 1].get(id); const w = nodeW(pair + 1, cell); nodes.push({ x: cell.cx - w / 2, y: tTop - POSITION_NODE_H, w, h: POSITION_NODE_H, row: pair + 1, id }); }
  }
  return { paths, nodes };
}
